import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Container,
  Paper,
  Text,
  Loader,
  Title,
  Divider,
  Group,
  Stack,
  Badge,
  Button
} from '@mantine/core';
import moment from 'moment';
import { useGetPublicOrderReceipt } from '../../hooks/publics/queries';
import { generateTicketHTMLString } from '../../utils/ticketHelper';
import { useTranslation } from 'react-i18next';
import OrderStatuses from '../../enum/OrderStatuses';
import {
  EvidenceCarousel,
  ZoomImage
} from '../../components/OrderModals/OrderEvidencesCarouselModal';
import { buildOrderFolio } from '../../utils/ordersUtils';
import TicketPreviewContent from '../../components/TicketPreviewContent/TicketPreviewContent';

interface PublicReceiptParams {
  publicToken: string;
}

const PublicReceipt = () => {
  const { publicToken } = useParams<PublicReceiptParams>();
  const { t } = useTranslation(['FieldName']);
  const [zoomSrc, setZoomSrc] = useState<string | null>(null);
  const [ticketHTMLString, setTicketHTMLString] = useState<string>('');
  const { data, isLoading, error } = useGetPublicOrderReceipt(publicToken, {
    retry: false
  });

  const { order, companyLogo } = data || {};
  // Add a function to handle sharing
  const handleShareReceipt = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: `Recibo ${buildOrderFolio(order?.folioPrefix, order?.sequence)}`,
          text: `Recibo de servicio en ${order.createdIn.name}`,
          url: window.location.href
        });
      } else {
        await navigator.clipboard.writeText(window.location.href);
        alert('Enlace copiado al portapapeles');
      }
    } catch (error) {
      console.error('Error sharing:', error);
    }
  };

  useEffect(() => {
    if (!order?.createdIn) return;

    let cancelled = false;

    const run = async () => {
      const html = await generateTicketHTMLString(
        order?.createdIn,
        order,
        companyLogo,
        t,
        false,
        false,
        order?.createdIn?.ticketStructure,
        order?.createdIn?.company?.companyTaxInfo || order?.createdIn?.companyTaxInfo
      );

      if (cancelled) return;
      setTicketHTMLString(html);
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [companyLogo, order, t]);

  if (isLoading) {
    return (
      <Container
        py="xl"
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh'
        }}>
        <Loader size="xl" />
      </Container>
    );
  }

  if (error || !order) {
    return (
      <Container py="xl">
        <Paper p="md" radius="md" shadow="sm">
          <Title order={3} ta="center" mb="md">
            Error
          </Title>
          <Text ta="center">{error?.message || 'No se pudo cargar el recibo'}</Text>
        </Paper>
      </Container>
    );
  }

  return (
    <>
      <Group grow justify="center" p="xl" align="flex-start" gap="xl">
        <Paper p="md" radius="md" shadow="sm" w="100%" maw={350}>
          <Stack gap="sm">
            {/* Share Button and Badge Header */}
            <Group justify="flex-end">
              <Badge color={order.status === OrderStatuses.COLLECTED ? 'green' : 'blue'}>
                {`${t(
                  `FieldName:orderStatuses.${
                    OrderStatuses[order.status.toUpperCase() as keyof typeof OrderStatuses]
                  }`
                )}`}
              </Badge>
              <Button variant="light" onClick={handleShareReceipt} size="sm">
                Compartir
              </Button>
            </Group>
            <Divider />
            {/* Ticket */}
            <TicketPreviewContent
              mode="html"
              content={ticketHTMLString}
            />
            {/* Footer */}
            <Divider />
            <Text size="xs" ta="center" c="dimmed">
              Recibo generado el {moment().format('DD/MM/YYYY HH:mm')}
            </Text>
            <Text size="xs" ta="center" c="dimmed">
              Powered by Washday
            </Text>
          </Stack>
        </Paper>
        {order?.evidence?.length > 0 && (
          <EvidenceCarousel evidence={order?.evidence} setZoomSrc={setZoomSrc} />
        )}
      </Group>
      <ZoomImage zoomSrc={zoomSrc} setZoomSrc={setZoomSrc} />
    </>
  );
};

export default PublicReceipt;
